import type {
  InstantInsightsChart,
  SocialAnalyseChart,
} from "shared/modules/Charts/types/chartTypes";
import { type Nullable } from "shared/types";

export enum Region {
  APAC = "apac",
  AU = "au",
  CA = "ca",
  DACH = "dach",
  FR = "fr",
  NORDIC = "nordic",
  NZ = "nz",
  UK = "uk",
  US = "us",
}

export interface CisionSpecificConfig {
  churnzero?: {
    appKey: string;
    scriptUrl: string;
  };
  urls?: {
    learnMore?: string;
  };
}

export interface BaseFeatureTeaser {
  imageUrl?: string;
  moreInfoUrl?: string;
}

export interface AppConfigMenuLogo {
  isDarkMode?: boolean;
  path: string;
  secondHalf?: {
    path: string;
    style?: string;
  };
  subtitle?: string;
}

export interface FeatureTeaser extends BaseFeatureTeaser {
  region?: {
    [key in Region]?: BaseFeatureTeaser;
  };
}

export interface AppConfig {
  adminUserLogrocketAppId: string;
  aiAssistant: string;
  baseUrl: string;
  churnzero: {
    appKey: string;
    scriptUrl: string;
  };
  contentDateCap: number;
  emails: {
    accountManagers: string;
    bd: string;
    customerSuccess: string;
    dedicatedSupport: string;
    help: string;
    streemSocial: string;
  };
  featureTeasers?: {
    [key: string]: FeatureTeaser;
  };
  firebaseAppConfig: {
    apiKey: string;
    appId: string;
    authDomain: string;
    projectId: string;
  };
  geoChartANZOnly: boolean;
  includePrintPdfClippingScheduledReports: boolean;
  includePrintPdfClippingStreamReports: boolean;
  insightsPlusTeaser?: {
    factSheetImageUrl: string;
    factSheetUrl: string;
    imageUrl: string;
    reportImageUrl: string;
    reportUrl: string;
  };
  insightsReports: {
    requiresTopicValidation: boolean;
  };
  instantInsightsCharts: Array<InstantInsightsChart>;
  intercomAppId: Nullable<string>;
  legalName: string;
  login: {
    findOutMoreLink: string;
    logo: string;
    privacyLink: string;
    video: string;
  };
  logo: {
    login: string;
    menu: {
      insights: AppConfigMenuLogo;
      media: AppConfigMenuLogo;
      outreach?: AppConfigMenuLogo;
      social: AppConfigMenuLogo;
    };
  };
  logrocketAppId: string;
  mediaReleaseSendingDomain: string;
  microsoftClientId: string;
  microsoftTeamsInstallLink: string;
  mobileTrapImage: string;
  mobileUrlScheme: string;
  organisationMaxDateRange: number;
  pendo: {
    publicAppId: string;
  };
  phone: {
    formatted: string;
    raw: string;
  };
  platformIcons: {
    narratives: string;
  };
  platformName: string;
  segmentWriteKey: string;
  shareableLinksDomain: string;
  slackClientId: string;
  socialAnalyseCharts: Array<SocialAnalyseChart>;
  streamNotificationSpikeImages: {
    large: string;
    medium: string;
    small: string;
  };
  urls: {
    aiAssistant: string;
    booleanCreator: { expandHelp: string; refineHelp: string };
    contentWarning: string;
    googleAnalyticsHelp: string;
    helpArticles?: string;
    learnMore: string;
    legalTerms: string;
    mentionRestriction: string;
    mobileApp: { android: string; ios: string };
    onboardingCustomerSuccess: string;
    outreachHelpArticles: string;
    sentimentRating: string;
    slackHelp: string;
    slackLearnMore: string;
    spikeHelp: string;
    storageAttachmentsBase: string;
    taggedEmailReport: string;
    viewPublicHolidays: string;
  };
}
